// cSpell:ignore afrekening Eigenaar gebouw Keuze kosten oppervlakte Ouder panden Provisies Samenvatting Toon Verdeling volgens voogd
/* eslint-disable no-nested-ternary */
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Card,
  DisplayText,
  Icon,
} from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Money } from "../../../../components/Money";
import { vatOfAmount } from "../../../../money.utils";
import { formatMoney } from "../../../../moneyText.utils";
import { IOwnerKeyShare, ownerCardNote } from "../../../../ownerShare.utils";
import { joinPartyNames } from "../../../../settlementView.utils";
import { Category, Invoice, vatPercent } from "../../../../types";
import { InfoTip } from "./InfoTip";
import { CELL_PADDING } from "./provisionColumns";

// An amount as text ("€ 1.997,26"), for a sentence that names it; a screen
// shows an amount itself with Money (components/Money.tsx).
export const fmt = formatMoney;

// An amount in a table cell, in the financial font (Money).
export const moneyCell = (n: number): React.ReactNode => <Money amount={n} />;

export {
  fmtDate,
  overlapsPeriod,
  rentedPeriodLabel,
  withinPeriod,
} from "./dates";

// The cost's VAT, rounded half-up on its cents like the engine (toFixed
// rounded 55,50 at 21% to 11,65 where the cost popup showed 11,66).
export const btwOf = (inv: Invoice) =>
  vatOfAmount({
    amountExclVat: inv.amountExclVat,
    vatPct: vatPercent(inv.vatPct),
  });

// Shared wrapper for settlement-style detail tables (cost distribution +
// "Toon details"): extra vertical cell padding and clear column spacing so the
// many-column tables read uniformly across the cost view and the afrekening.
export const DetailTableWrap = styled.div`
  th,
  td {
    padding-top: 12px;
    padding-bottom: 12px;
    vertical-align: top;
  }
  th:not(:first-child),
  td:not(:first-child) {
    padding-left: 16px;
  }
  /* Heavier continuous line above the totals row — signals "sum of the above".
     DataTable renders the first totals cell inside a <span> (Clickable
     wrapper), which breaks per-cell borders. box-shadow on the <tr> draws one
     unbroken line regardless of cell structure. */
  tfoot tr {
    box-shadow: inset 0 2px 0 ${({ theme }) => theme.getColor("gray", 800)};
  }
  tfoot td,
  tfoot th {
    border-bottom-width: 0 !important;
    font-weight: ${FontWeights.SemiBold};
    background: ${({ theme }) => theme.getColor("gray", 50)};
  }
`;

// Category → Rentio icon source. Falls back to a generic document icon.
export const categoryIcon = (category: Category): any => {
  switch (category) {
    case "Water":
      return "waterTap";
    case "Electricity":
      return "lightBulb";
    case "Heating":
    case "HeatingOil":
      return "fire";
    case "Gas":
      return "hotFlame";
    case "Maintenance":
    case "MinorRepairs":
    case "MajorRepairs":
    case "Elevator":
    case "Cleaning":
      return "houseWrench";
    case "PropertyTax":
    case "Taxes":
      return "currencyEuroCircle";
    case "Management":
    case "SyndicProvision":
    case "SyndicSettlement":
      return "buildings";
    default:
      return "accountingDocument";
  }
};

export const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Banner variation="info" hasDismiss={false}>
    {children}
  </Banner>
);

export const Warn: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Banner variation="warning" hasDismiss={false}>
    {children}
  </Banner>
);

export const StepHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div style={{ marginBottom: 16 }}>
    <DisplayText size="medium">{children}</DisplayText>
  </div>
);

// Re-exported from the engine so the wizard, the statement and the settlement
// can never disagree on a day count. A second local copy is exactly how the
// provision month maths drifted apart before.
export { overlapDays } from "../../../../engine";

// The title of a card of the wizard: the collapsible cards' and the
// distribution step's, so the steps' cards read alike.
export const cardTitleStyle = css`
  font-size: 18px;
  font-weight: ${FontWeights.Medium};
  color: ${({ theme }) => theme.getColor("gray", 1000)};
`;

const CollapsibleHeader = styled.div<{ expanded?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  margin-bottom: ${({ expanded }) => (expanded ? "16px" : "0")};
  .titleWrap {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .title {
    ${cardTitleStyle}
  }
  .subtitle {
    color: ${({ theme }) => theme.getColor("gray", 600)};
  }
  .right {
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${({ theme }) => theme.getColor("gray", 600)};
  }
`;

const PartyName = styled.span`
  font-weight: ${FontWeights.SemiBold};
`;

const PartyNote = styled.span`
  display: block;
  margin-top: 2px;
  font-weight: ${FontWeights.Regular};
  font-size: 13px;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

// Card title for one party of a settlement: "{address} - {party}" in bold,
// the address first (Lewis, 2026-10-17); without an address the party alone.
// An optional note goes on its own line (e.g. the parent on the lease).
export const PartyTitle: React.FC<{
  name: string;
  address?: string;
  note?: React.ReactNode;
}> = ({ name, address, note }) => (
  <>
    <PartyName>{address ? `${address} - ${name}` : name}</PartyName>
    {note ? <PartyNote>{note}</PartyNote> : null}
  </>
);

// "Ouder/voogd: Paul Parent" under a tenant on a lease that also names a
// parent or guardian, so the broker sees everyone on it.
export const parentNote = (parentNames?: string[]): string | undefined =>
  parentNames && parentNames.length > 0
    ? getLocalizedText("settlement.summary.parent_on_lease", {
        names: joinPartyNames(parentNames),
      })
    : undefined;

// "Eigenaar van 4 panden · 55/85 van het gebouw (Verdeling volgens
// oppervlakte)" under the card of an owner of several units, each unit's
// full address and weight in its "i" (Lewis, 2026-10-19: "bus 1, 2, 3 en
// 7" named nothing for a unit without a box; how do we know what each
// owner owns?). An owner of one unit has its address in the card's title,
// and only its share under it (see ownerCardNote).
export const ownerUnitsNote = (params: {
  units: { unitId: string; address: string }[];
  shares?: IOwnerKeyShare[];
}): React.ReactNode => {
  const { subtitle, lines } = ownerCardNote({
    units: params.units,
    shares: params.shares ?? [],
  });
  if (!subtitle) return undefined;
  return (
    <>
      {subtitle}
      <InfoTip rows={[]} lines={lines} />
    </>
  );
};

// One collapsible settlement card — the shared chrome (title + optional right
// content + chevron) used by every wizard step (Keuze van kosten, Provisies,
// Samenvatting) so the cards read identically and all fold open/closed.
export const CollapsibleCard: React.FC<{
  title: React.ReactNode;
  // Optional line shown under the title (full-width title band).
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  defaultCollapsed?: boolean;
  // Optional totals — rendered inside the same card, below the body and set off
  // by a divider, and folded away together with the body when the card collapses.
  totals?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  title,
  subtitle,
  right,
  defaultCollapsed = false,
  totals,
  children,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <Card>
      <CollapsibleHeader
        expanded={!collapsed}
        onClick={() => setCollapsed(c => !c)}
      >
        <span className="titleWrap">
          <span className="title">{title}</span>
          {subtitle != null && <span className="subtitle">{subtitle}</span>}
        </span>
        <span className="right">
          {right}
          <Icon source={collapsed ? "chevronDown" : "chevronUp"} />
        </span>
      </CollapsibleHeader>
      {!collapsed && children}
      {!collapsed && totals != null && <TotalsDivider>{totals}</TotalsDivider>}
    </Card>
  );
};

export const TotalsDivider = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  /* Match a table cell's right padding (DataTable's and the grouped
     table's are both CELL_PADDING) so the total amount lines up vertically
     with the amount column above it. */
  padding-right: ${CELL_PADDING};
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
`;
